import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, Scene } from "./types";

// Lesson: Policies → Policy checks. Fixture `policyCheck`: a finished Riverton
// PD use of force report that cites policy inline, holds one policy
// placeholder (supervisor notification, Policy 300.7), and has two
// "[Policy check · ...]" suggestions (300.7 and 300.6) beside an ordinary one.
// The scene opens the 300.7 section from its badge, types the supervisor into
// the placeholder, and re-evaluates: the 300.7 item clears, 300.6 stays.
// Timings below come from the clips (ffmpeg silencedetect).
const CASE_ID = "c0000000-0000-4000-8000-000000000221";
const PLACEHOLDER = `${sel("editorNarrative")} .replace-text`;
const FORCE_PARAGRAPH = `${sel("editorNarrative")} p:nth-of-type(3)`;
const POLICY_ITEM = `${sel("suggestionItem")}:has(${sel("policyBadge")})`;
const BACK = `${sel("policySection")} button`;

const scene: Scene = {
  id: "policy-check-suggestions",
  title: "Policy checks",
  startRoute: `/cases/${CASE_ID}`,
  fixture: "policyCheck",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "Every report you write is compared against your own department's policy, using the specific section that applies to that incident. This is a use of force report at Riverton PD. Let's see how the policy shows up.",
      actions: [
        { type: "waitFor", target: sel("reportScore") },
        { type: "spotlight", target: null },
        { type: "wait", ms: 1500 },
      ],
    },
    {
      id: "draft",
      narration:
        "When the draft was written, the incident was classified and the matching policy sections were pulled in. The draft cites them inline, with the title, the section number, and the heading. And where a policy requires a detail I never gave, it left a placeholder instead of guessing. This one says it's required by Policy 300.7.",
      actions: [
        // Inline citation is said at 5.6 s, the placeholder sentence at 10.4 s,
        // "This one says" at 15.7 s; the clip is 19.1 s.
        { type: "scrollIntoView", target: FORCE_PARAGRAPH },
        { type: "moveTo", target: FORCE_PARAGRAPH },
        { type: "wait", ms: 4600 },
        { type: "spotlight", target: FORCE_PARAGRAPH },
        { type: "wait", ms: 4300 },
        { type: "spotlight", target: null },
        { type: "scrollIntoView", target: PLACEHOLDER },
        { type: "moveTo", target: PLACEHOLDER },
        { type: "wait", ms: 700 },
        { type: "spotlight", target: PLACEHOLDER },
        { type: "wait", ms: 6000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "check",
      narration:
        "After the draft, the policy check reads the narrative against those same sections. Whatever is missing, or only partly covered, shows up in Suggestions, with the section it comes from underneath. The placeholder marks the hole in the text. The suggestion explains which policy wants it.",
      actions: [
        // Suggestions are named at 4.8 s, "underneath" at 9.1 s, the
        // placeholder sentence at 11.4 s, the last sentence at 14.0 s.
        { type: "scrollIntoView", target: POLICY_ITEM },
        { type: "wait", ms: 3800 },
        { type: "moveTo", target: POLICY_ITEM },
        { type: "spotlight", target: POLICY_ITEM },
        { type: "wait", ms: 3800 },
        { type: "moveTo", target: sel("policyBadge") },
        { type: "wait", ms: 1900 },
        { type: "spotlight", target: PLACEHOLDER },
        { type: "wait", ms: 2400 },
        { type: "spotlight", target: POLICY_ITEM },
        { type: "wait", ms: 2400 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "open",
      narration:
        "Click the section badge to read the policy itself. It opens right here in the sidebar. Section 300.7 asks for the name of the supervisor notified, and the time. That's exactly what's missing.",
      actions: [
        // "Click the section badge" at 0 s; the section is read from 5.1 s.
        { type: "moveTo", target: sel("policyBadge") },
        { type: "wait", ms: 1500 },
        { type: "click", target: sel("policyBadge") },
        { type: "waitFor", target: sel("policySection") },
        { type: "wait", ms: 1800 },
        { type: "spotlight", target: sel("policySection") },
        { type: "wait", ms: 5200 },
        { type: "spotlight", target: null },
        { type: "wait", ms: 1000 },
      ],
    },
    {
      id: "fix",
      narration:
        "Back to the suggestions, then the placeholder. I'll type the supervisor and the time: Sergeant Delgado, notified at twenty-three thirty-one hours.",
      actions: [
        // "then the placeholder" at 1.5 s; typing starts as the name is said (5.3 s).
        { type: "moveTo", target: BACK },
        { type: "wait", ms: 100 },
        { type: "click", target: BACK },
        { type: "waitFor", target: sel("reEvaluate") },
        { type: "wait", ms: 500 },
        { type: "moveTo", target: PLACEHOLDER },
        { type: "wait", ms: 2400 },
        { type: "typeOver", target: PLACEHOLDER, text: "Sergeant Delgado, notified at 2331 hours", cps: 13 },
        { type: "wait", ms: 600 },
      ],
    },
    {
      id: "reeval",
      narration:
        "Now Re-evaluate. The policy check runs again on the new text, and the supervisor item clears. The medical aid item stays, because the report still doesn't say whether I asked Kessler about his wrist.",
      actions: [
        // Click lands on "Re-evaluate" (about 1 s); the list updates by about 4.5 s;
        // the medical item is discussed from 6.6 s.
        { type: "moveTo", target: sel("reEvaluate") },
        { type: "wait", ms: 500 },
        { type: "click", target: sel("reEvaluate") },
        { type: "wait", ms: 4800 },
        { type: "spotlight", target: POLICY_ITEM },
        { type: "wait", ms: 4000 },
        { type: "spotlight", target: null },
        { type: "wait", ms: 800 },
      ],
    },
    {
      id: "address",
      narration:
        "When several are open, Address Suggestions lets you answer them by recording or uploading audio, and the AI writes your answers into the right places for you to review.",
      actions: [
        // "Address Suggestions" is said at 1.6 s; the menu stays open for the rest.
        { type: "moveTo", target: sel("addToReportButton") },
        { type: "wait", ms: 900 },
        { type: "click", target: `${sel("addToReportButton")} button` },
        { type: "wait", ms: 5600 },
        { type: "click", target: sel("reportScore") },
        { type: "wait", ms: 1200 },
      ],
    },
    {
      id: "outro",
      narration:
        "If your department hasn't uploaded any documents, none of this happens: no policy citations, no policy placeholders, and no policy check items. And a policy check item is a reading of the policy, not a finding, so if it doesn't apply to the incident, leave it.",
      actions: [
        // Nothing to show for the first sentence; the second starts at 9.4 s.
        { type: "wait", ms: 9000 },
        { type: "moveTo", target: POLICY_ITEM },
        { type: "spotlight", target: POLICY_ITEM },
        { type: "wait", ms: 6000 },
        { type: "spotlight", target: null },
      ],
    },
  ],
};

export default scene;
